import React, { useState, useEffect } from 'react';
import api from '../services/api';
import ReactMarkdown from 'react-markdown';
import { Plus, Check, Xmark } from 'iconoir-react';

interface LegalDocument {
  id: number;
  type: string;
  version: string;
  content: string;
  is_active: boolean;
  effective_date: string | null;
  deactivated_at: string | null;
  created_at: string;
}

const getTypeLabel = (type: string) => {
  const labels: { [key: string]: string } = {
    cgv: 'CGV',
    cgu: 'CGU',
    privacy_policy: 'Politique de confidentialité',
    cookies_policy: 'Politique des cookies',
  };
  return labels[type] || type;
};

const DocumentCard: React.FC<{ doc: LegalDocument; onClick: () => void; onActivate?: (e: React.MouseEvent) => void }> = ({ doc, onClick, onActivate }) => (
  <div className="document-card" onClick={onClick} style={{ cursor: 'pointer' }}>
    <div className="document-header">
      <div>
        <h3>{getTypeLabel(doc.type)}</h3>
        <span className="document-version">Version {doc.version}</span>
      </div>
      <div className="document-actions">
        {doc.is_active ? (
          <span className="badge badge-success">
            <Check width={16} height={16} strokeWidth={2} />
            Actif
          </span>
        ) : (
          <button
            className="btn btn-sm btn-secondary"
            onClick={onActivate}
            title="Activer ce document"
          >
            Activer
          </button>
        )}
      </div>
    </div>
    <div className="document-footer">
      <small>Créé le {new Date(doc.created_at).toLocaleDateString('fr-FR')}</small>
      {doc.effective_date && (
        <small> · En vigueur depuis le {new Date(doc.effective_date).toLocaleDateString('fr-FR')}</small>
      )}
      {doc.deactivated_at && (
        <small> · Désactivé le {new Date(doc.deactivated_at).toLocaleDateString('fr-FR')}</small>
      )}
    </div>
  </div>
);

const LegalManagement: React.FC = () => {
  const [documents, setDocuments] = useState<LegalDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<LegalDocument | null>(null);
  const [formData, setFormData] = useState({
    type: 'cgv',
    version: '',
    content: '',
    is_active: false,
  });

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      const response = await api.get('/admin/legal-documents');
      setDocuments(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (doc: LegalDocument) => {
    try {
      await api.put(`/admin/legal-documents/${doc.id}`, { is_active: true });
      await loadDocuments();
    } catch (error) {
      console.error('Erreur lors de l\'activation:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/admin/legal-documents', formData);
      await loadDocuments();
      setShowForm(false);
      setFormData({ type: 'cgv', version: '', content: '', is_active: false });
      alert('Document créé avec succès');
    } catch (error) {
      console.error('Erreur lors de la création:', error);
      alert('Erreur lors de la création du document');
    }
  };

  const activeDocs = documents.filter(doc => doc.is_active);
  const archivedDocs = documents
    .filter(doc => !doc.is_active)
    .sort((a, b) => {
      const dateA = a.deactivated_at ? new Date(a.deactivated_at).getTime() : 0;
      const dateB = b.deactivated_at ? new Date(b.deactivated_at).getTime() : 0;
      return dateB - dateA;
    });

  if (loading) return <div>Chargement...</div>;

  return (
    <div className="legal-management">
      <div className="section-header">
        <h2>Documents juridiques</h2>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
          <Plus width={18} height={18} strokeWidth={2} />
          Nouveau document
        </button>
      </div>

      {showForm && (
        <div className="form-card">
          <h3>Créer un document juridique</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Type de document</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className="form-control"
              >
                <option value="cgv">CGV</option>
                <option value="cgu">CGU</option>
                <option value="privacy_policy">Politique de confidentialité</option>
                <option value="cookies_policy">Politique des cookies</option>
              </select>
            </div>
            <div className="form-group">
              <label>Version</label>
              <input
                type="text"
                value={formData.version}
                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                className="form-control"
                placeholder="Ex: 1.0"
                required
              />
            </div>
            <div className="form-group">
              <label>Contenu (Markdown)</label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                className="form-control"
                rows={10}
                placeholder="Contenu du document en Markdown..."
                required
              />
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
                {' '}Activer immédiatement
              </label>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">Créer</button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Documents actifs */}
      <div className="section-header" style={{ marginTop: '32px' }}>
        <h3>Documents actifs</h3>
      </div>
      {activeDocs.length === 0 ? (
        <p style={{ color: '#666' }}>Aucun document actif.</p>
      ) : (
        <div className="documents-list">
          {activeDocs.map(doc => (
            <DocumentCard key={doc.id} doc={doc} onClick={() => setSelectedDoc(doc)} />
          ))}
        </div>
      )}

      {/* Documents archivés */}
      {archivedDocs.length > 0 && (
        <>
          <div className="section-header" style={{ marginTop: '40px' }}>
            <h3>Documents archivés</h3>
          </div>
          <div className="documents-list">
            {archivedDocs.map(doc => (
              <DocumentCard key={doc.id} doc={doc} onClick={() => setSelectedDoc(doc)} onActivate={(e) => { e.stopPropagation(); toggleActive(doc); }} />
            ))}
          </div>
        </>
      )}

      {/* Modal lecture markdown */}
      {selectedDoc && (
        <div
          className="modal-backdrop"
          onClick={(e) => { if (e.target === e.currentTarget) setSelectedDoc(null); }}
        >
          <div className="modal-container" style={{ maxWidth: '800px', padding: '32px', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
              <div>
                <h2 style={{ margin: 0 }}>{getTypeLabel(selectedDoc.type)} — v{selectedDoc.version}</h2>
                {selectedDoc.effective_date && (
                  <small style={{ color: '#666' }}>
                    En vigueur depuis le {new Date(selectedDoc.effective_date).toLocaleDateString('fr-FR')}
                  </small>
                )}
              </div>
              <button className="btn btn-secondary" onClick={() => setSelectedDoc(null)}>
                <Xmark width={18} height={18} />
              </button>
            </div>
            <div className="markdown-content">
              <ReactMarkdown>{selectedDoc.content}</ReactMarkdown>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LegalManagement;
